'use client';

import { useRouter } from 'next/navigation';
import { useQueries } from '@tanstack/react-query';
import {
  Bot, Brain, Plug, Server, Download, FolderPlus,
} from 'lucide-react';
import { Button } from '@/components/custom/button';
import { DataCard } from '@/components/custom/data-card';
import { Skeleton } from '@/components/custom/skeleton';
import { EmptyState } from '@/components/custom/empty-state';
import { StatusBadge } from '@/components/custom/status-badge';
import { useProject } from '@/contexts/project-context';
import { useScopeStore } from '@/lib/stores/scope-store';
import { rbac } from '@/components/settings/_shared';
import { useAuth } from '@/lib/hooks/useAuth';
import { mcpService } from '@/lib/api/services/mcp';
import { dataServiceApi } from '@/lib/api/services/dataservice';
import { agentsService } from '@/lib/api/services/agents';
import { ltmemoryService } from '@/lib/api/ltmemory';
import { ConnectAgentsCard } from '@/components/home/connect-agents-card';


function useGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

export default function DashboardPage() {
  const router = useRouter();
  const { selectedProject, projects, loadingProjects } = useProject();
  const { user } = useAuth();
  // Module role from the session bootstrap — decides whether the zero-project
  // empty state offers "Create project" or tells them to ask an admin.
  const role = useScopeStore((s) => s.role);
  const greeting = useGreeting();

  // Spec header: "Good morning, {name}." · "Enterprise Context Platform · {project}".
  // Prefer firstName; else derive a clean first name from username/email
  // (e.g. "venkata.kethineni@wexa.ai" → "Venkata") rather than showing a raw email.
  const firstName = (() => {
    const fn = user?.firstName?.trim();
    if (fn) return fn;
    const raw = user?.username?.trim() || user?.email?.trim() || '';
    if (!raw) return '';
    const token = (raw.includes('@') ? raw.split('@')[0] : raw).split(/[\s._-]+/)[0];
    return token ? token.charAt(0).toUpperCase() + token.slice(1) : '';
  })();
  const headerTitle = firstName ? `${greeting}, ${firstName}.` : `${greeting}.`;
  const headerSub = selectedProject?.name
    ? `Enterprise Context Platform · ${selectedProject.name}`
    : 'Enterprise Context Platform';

  const pid = selectedProject?.id ?? null;
  const encPid = pid ? encodeURIComponent(pid) : null;

  // These are dashboard *summary* widgets: each renders its own graceful
  // 'Error' / '–' / '0' fallback from `isError` below. They must NOT escalate to
  // the global error boundary (QueryProvider's `throwOnError`) — otherwise a
  // single transient failure blanks the whole app with "Something went wrong".
  // This notably happens right after onboarding: the just-created project's
  // data-service/harness context isn't warm yet, so these project-scoped reads
  // 404 for a beat before the dashboard would otherwise render. Opt out so the
  // home page degrades instead of crashing (mirrors useConnectionInfo).
  const [mcpQuery, connectorStatsQuery, memoriesQuery, agentsQuery] = useQueries({
    queries: [
      {
        queryKey: ['mcp-servers-summary'],
        queryFn: () => mcpService.listServers(),
        staleTime: 30_000,
        retry: 1,
        throwOnError: false,
      },
      {
        queryKey: ['connector-stats', pid],
        queryFn: () => dataServiceApi.getConnectorStats(pid!),
        staleTime: 30_000,
        refetchInterval: 30_000,
        enabled: !!pid,
        retry: 1,
        throwOnError: false,
      },
      {
        queryKey: ['memories-count', pid],
        queryFn: () => ltmemoryService.listMemories(pid!, { limit: 1 }),
        staleTime: 60_000,
        enabled: !!pid,
        retry: 1,
        throwOnError: false,
      },
      {
        queryKey: ['agents-count', pid],
        queryFn: () => agentsService.listAgents(pid!),
        staleTime: 30_000,
        enabled: !!pid,
        retry: 1,
        throwOnError: false,
      },
    ],
  });

  // MCP
  const mcpServers = mcpQuery.data?.servers ?? [];
  const activeMcp = mcpServers.filter(s => s.status === 'active').length;
  const errorMcp  = mcpServers.filter(s => s.status === 'error' || s.status === 'unhealthy').length;

  // Connectors
  const connStats = connectorStatsQuery.data;
  const totalConnectors = connStats?.total_connectors ?? 0;
  const activeConnectors = connStats?.active_connectors ?? 0;
  const connectorValue = connectorStatsQuery.isLoading ? '–'
    : connectorStatsQuery.isError ? 'Error'
    : totalConnectors === 0 ? '0'
    : `${activeConnectors} / ${totalConnectors}`;
  const connectorStatus: 'active' | 'inactive' | 'warning' =
    !pid ? 'inactive' :
    connectorStatsQuery.isLoading ? 'inactive' :
    totalConnectors === 0 ? 'inactive' :
    activeConnectors === totalConnectors ? 'active' : 'warning';

  // Memories
  const totalMemories = memoriesQuery.data?.total ?? 0;
  const memoriesValue = memoriesQuery.isLoading ? '–'
    : memoriesQuery.isError ? 'Error'
    : String(totalMemories);

  // Agents — count of configured agents in the project (real data; no deltas).
  const totalAgents = agentsQuery.data?.length ?? 0;
  const agentsValue = !pid ? '0'
    : agentsQuery.isLoading ? '–'
    : agentsQuery.isError ? 'Error'
    : String(totalAgents);

  // Ingestion — derived from connector stats (no separate endpoint needed)
  const ingestionStatus: 'active' | 'inactive' | 'warning' =
    !pid || connectorStatsQuery.isLoading ? 'inactive' :
    activeConnectors > 0 ? 'active' :
    totalConnectors > 0 ? 'warning' : 'inactive';
  const ingestionValue = connectorStatsQuery.isLoading ? '…'
    : !pid ? 'No project'
    : activeConnectors > 0 ? `${activeConnectors} connector${activeConnectors > 1 ? 's' : ''} polling`
    : totalConnectors > 0 ? 'Stopped'
    : 'No connectors';

  if (!loadingProjects && projects.length === 0) {
    // "Zero projects" means two different things: a founder who must create the
    // first one, or an invitee with nothing scoped to them yet. Telling the
    // second group to "create your first project" is wrong twice over — the
    // workspace already exists, and a non-admin's create would be rejected.
    const canCreate = rbac.canCreateProject(role);

    return (
      <div className="min-h-[calc(100vh-57px)] flex items-center justify-center">
        <EmptyState
          icon={FolderPlus}
          title={canCreate ? 'Create your first project' : 'No projects yet'}
          description={
            canCreate
              ? 'Projects organize your memories, connectors, and AI sessions. Get started by creating one.'
              : 'You have not been given access to a project in this organization yet. An administrator can add you to one.'
          }
          action={
            canCreate ? (
              <Button variant="primary" onClick={() => router.push('/settings/projects')}>
                <FolderPlus className="w-4 h-4" aria-hidden="true" />
                Create project
              </Button>
            ) : undefined
          }
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cs-surface-page">
      <div
        className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(139,92,246,0.10),transparent)]"
        aria-hidden="true"
      />

      <div className="relative max-w-5xl mx-auto px-6 py-10 space-y-8">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-cs-text-primary">{headerTitle}</h1>
          <p className="text-sm text-cs-text-muted mt-0.5">{headerSub}</p>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {loadingProjects ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="stat-card" />)
          ) : (
            <>
              <DataCard
                label="Active Agents"
                value={agentsValue}
                icon={Bot}
                iconColor="text-cs-primary-400"
                onClick={() => router.push('/orchestrate/agents')}
              />
              <DataCard
                label="MCP Servers"
                value={mcpQuery.isLoading ? '–' : `${activeMcp} / ${mcpServers.length}`}
                icon={Server}
                iconColor={errorMcp > 0 ? 'text-cs-error-400' : 'text-cs-accent-400'}
                onClick={() => router.push('/mcp-servers')}
              />
              <DataCard
                label="Connectors"
                value={connectorValue}
                icon={Plug}
                iconColor={totalConnectors === 0 ? 'text-cs-text-muted' : 'text-cs-success-400'}
                onClick={encPid ? () => router.push(`/projects/${encPid}/connectors`) : undefined}
              />
              <DataCard
                label="Memories"
                value={memoriesValue}
                icon={Brain}
                iconColor={totalMemories === 0 ? 'text-cs-text-muted' : 'text-cs-warning-400'}
                onClick={encPid ? () => router.push(`/projects/${encPid}/memories`) : undefined}
              />
            </>
          )}
        </div>

        {/* Quick start + System health */}
        <div className="grid md:grid-cols-2 gap-6">

          {/* Quick Start */}
          <div className="space-y-3">
            <h2 className="text-xs font-semibold text-cs-text-muted uppercase tracking-wide">Quick Start</h2>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Learn Mode',    icon: Brain,    color: 'text-cs-accent-400',   bg: 'bg-cs-accent-500/10  hover:bg-cs-accent-500/20  border-cs-accent-500/20',  href: encPid ? `/projects/${encPid}/learn-mode` : null, desc: 'Teach memories' },
                { label: 'Run Ingestion', icon: Download, color: 'text-cs-success-400',  bg: 'bg-cs-success-bg     hover:bg-cs-success-500/20 border-cs-success-500/20',  href: encPid ? `/projects/${encPid}/ingestion` : null, desc: 'Sync data' },
                { label: 'Add Connector', icon: Plug,     color: 'text-cs-warning-400',  bg: 'bg-cs-warning-bg     hover:bg-cs-warning-500/20 border-cs-warning-500/20',  href: '/connect/marketplace', desc: 'Connect source' },
              ].map(({ label, icon: Icon, color, bg, href, desc }) => (
                <button
                  key={label}
                  onClick={() => href && router.push(href)}
                  disabled={!href}
                  className={`flex items-start gap-3 p-3 rounded-lg border transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed ${bg}`}
                >
                  <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${color}`} aria-hidden="true" />
                  <div>
                    <p className={`text-xs font-semibold ${color}`}>{label}</p>
                    <p className="text-[11px] text-cs-text-muted mt-0.5">{desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* System Health */}
          <div className="space-y-3">
            <h2 className="text-xs font-semibold text-cs-text-muted uppercase tracking-wide">System Health</h2>
            <div className="bg-cs-surface-card border border-cs-border rounded-lg divide-y divide-cs-border">
              <HealthRow
                label="Agent Runtime"
                icon={Bot}
                value={!pid ? 'No project'
                  : agentsQuery.isLoading ? '…'
                  : `${totalAgents} agent${totalAgents === 1 ? '' : 's'}`}
                status={totalAgents > 0 ? 'active' : 'inactive'}
                onClick={() => router.push('/orchestrate/agents')}
              />
              <HealthRow
                label="MCP Servers"
                icon={Server}
                value={mcpQuery.isLoading ? '…' : `${activeMcp} / ${mcpServers.length} active`}
                status={errorMcp > 0 ? 'error' : activeMcp === mcpServers.length && mcpServers.length > 0 ? 'active' : 'warning'}
                onClick={() => router.push('/mcp-servers')}
              />
              {encPid && (
                <>
                  <HealthRow
                    label="Connectors"
                    icon={Plug}
                    value={connectorStatsQuery.isLoading ? '…'
                      : totalConnectors === 0 ? 'None configured'
                      : `${activeConnectors} active · ${totalConnectors} total · ${connStats?.total_actions ?? 0} actions`}
                    status={connectorStatus}
                    onClick={() => router.push(`/projects/${encPid}/connectors`)}
                  />
                  <HealthRow
                    label="Ingestion"
                    icon={Download}
                    value={ingestionValue}
                    status={ingestionStatus}
                    onClick={() => router.push(`/projects/${encPid}/ingestion`)}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Connect your agents — MCP / API / SDK coordinates from the api-gateway */}
        <ConnectAgentsCard />

      </div>
    </div>
  );
}

function HealthRow({ label, icon: Icon, value, status, onClick }: {
  label: string; icon: React.ElementType; value: string;
  status: 'active' | 'inactive' | 'error' | 'warning'; onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-cs-surface-hover transition-colors text-left"
    >
      <Icon className="w-4 h-4 text-cs-text-muted flex-shrink-0" aria-hidden="true" />
      <span className="flex-1 text-xs text-cs-text-secondary">{label}</span>
      <span className="text-[11px] text-cs-text-muted mr-2">{value}</span>
      <StatusBadge variant={status} showIcon={true} />
    </button>
  );
}
